import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, X, Loader2 } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Modal 1-5 estrelas + comentário + toggle allow_public.
 * Reutilizado em MyCases (card) e CaseView (CTA no topo do caso).
 * Após salvar volta a `pending` — admin re-aprova.
 */
export const ReviewModal = ({ caseId, protocol, patientName, initial, onClose, onSaved }) => {
  const [stars, setStars] = useState(initial?.stars || 0);
  const [comment, setComment] = useState(initial?.comment || "");
  const [allowPublic, setAllowPublic] = useState(initial?.allow_public !== false);
  const [saving, setSaving] = useState(false);
  const [hover, setHover] = useState(0);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const submit = async () => {
    if (!stars) { toast.error("Selecione de 1 a 5 estrelas"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/review`, {
        stars, comment: comment.trim() || null, allow_public: allowPublic,
      });
      toast.success(initial ? "Avaliação atualizada · aguardando aprovação" : "Obrigado! Sua avaliação foi enviada para aprovação");
      onSaved?.(data.review);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar avaliação");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
         onClick={onClose} data-testid="review-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-slate-600" aria-label="Fechar">
          <X className="w-5 h-5" />
        </button>
        <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Avaliação da família</div>
        <h3 className="font-display font-bold text-xl text-[#0B2B62] mt-1">Como foi o atendimento?</h3>
        <p className="text-xs text-slate-500 mt-1">{protocol} · {patientName}</p>

        <div className="mt-5 flex items-center justify-center gap-1.5" data-testid="stars-row">
          {[1,2,3,4,5].map((n) => {
            const filled = (hover || stars) >= n;
            return (
              <button key={n} type="button"
                onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                onClick={() => setStars(n)}
                data-testid={`star-${n}`}
                className={`transition-transform hover:scale-110 ${filled ? "text-amber-400" : "text-slate-300"}`}>
                <Star className="w-10 h-10" fill={filled ? "currentColor" : "none"} strokeWidth={1.5} />
              </button>
            );
          })}
        </div>
        <div className="text-center text-xs text-slate-500 mt-1 h-4">
          {stars ? ["", "Ruim","Regular","Bom","Muito bom","Excelente"][stars] : "toque nas estrelas"}
        </div>

        <textarea value={comment} onChange={(e) => setComment(e.target.value)}
          data-testid="review-comment"
          placeholder="Conte pra gente como foi (opcional) — sua palavra ajuda outras famílias."
          rows={4} maxLength={600}
          className="mt-4 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-[#0B2B62] focus:outline-none resize-none" />

        <label className="mt-3 flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={allowPublic} onChange={(e) => setAllowPublic(e.target.checked)}
                 data-testid="review-allow-public"
                 className="mt-1 w-4 h-4 accent-[#0B2B62]" />
          <span className="text-xs text-slate-600 leading-relaxed">
            Autorizo mostrar essa avaliação publicamente no site do RAF Direct.
            <span className="block text-[10px] text-slate-400 mt-0.5">Só aparece seu primeiro nome + inicial do sobrenome — nunca o do paciente.</span>
          </span>
        </label>

        <button onClick={submit} disabled={saving || !stars}
          data-testid="review-submit"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#0B2B62] text-white rounded-lg font-bold text-sm hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {initial ? "Atualizar avaliação" : "Enviar avaliação"}
        </button>
        <p className="text-[10px] text-slate-400 text-center mt-2">
          Avaliações passam por moderação antes de aparecer no site.
        </p>
      </div>
    </div>
  );
};

export default ReviewModal;
